import { useLayoutEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom-v5-compat'

import { useAuthenticatedUser } from '@sparkai/auth'
import services, { getHttpStatus, shouldRetryShareLinkResolution } from '@sparkai/services/spark'

import { ShareLinkAccessState } from './ShareLinkAccessState'
import {
  buildAppUrl,
  buildNoAccessDestination,
  canOpenAdminShareLink,
  canOpenCustomerShareLink,
  ENGAGEMENT_NOT_FOUND_RESULT,
  getShareLinkTargetApp,
  UNSUPPORTED_ROLE_RESULT,
} from './shareLinkResolution'

type ShareLinkTargetApp = ReturnType<typeof getShareLinkTargetApp>

type DestinationBuilder = () => string

const canRedirectToTargetApp = (targetApp: ShareLinkTargetApp) => targetApp !== 'unsupported'
const shouldRedirectNotFound = (status: number | undefined) => status === 400 || status === 404
const shouldRedirectNoAccess = (status: number | undefined) => status === 401 || status === 403

const emptyEngagementDestinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>> = {
  admin: () => buildNoAccessDestination('/admin/', 'engagement-not-available', 'not-found'),
  'mission-specialist': () => buildNoAccessDestination('/ms/', 'engagement-not-available', 'not-found'),
}

const engagementNotFoundDestinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>> = {
  admin: () => buildNoAccessDestination('/admin/', 'engagement-not-available', 'not-found'),
  customer: () => buildNoAccessDestination('/customer/', 'engagement-not-available', 'not-found'),
  'mission-specialist': () => buildNoAccessDestination('/ms/', 'engagement-not-available', 'not-found'),
}

const engagementNoAccessDestinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>> = {
  admin: () => buildNoAccessDestination('/admin/', 'no-access', 'not-found'),
  customer: () => buildNoAccessDestination('/customer/', 'no-access'),
  'mission-specialist': () => buildNoAccessDestination('/ms/', 'no-access'),
}

function getDestination(
  destinations: Partial<Record<ShareLinkTargetApp, DestinationBuilder>>,
  targetApp: ShareLinkTargetApp
) {
  return destinations[targetApp]?.()
}

function getEmptyEngagementDestination(targetApp: ShareLinkTargetApp) {
  return getDestination(emptyEngagementDestinations, targetApp)
}

function getEngagementErrorDestination(status: number | undefined, targetApp: ShareLinkTargetApp) {
  if (shouldRedirectNotFound(status)) return getDestination(engagementNotFoundDestinations, targetApp)
  if (shouldRedirectNoAccess(status)) return getDestination(engagementNoAccessDestinations, targetApp)
}

function shouldWaitForEngagementRedirect(status: number | undefined, targetApp: ShareLinkTargetApp) {
  return (
    (shouldRedirectNotFound(status) || shouldRedirectNoAccess(status)) && canRedirectToTargetApp(targetApp)
  )
}

export function CanonicalEngagementLink() {
  const { engagementId } = useParams<'engagementId'>()
  const location = useLocation()
  const { profile } = useAuthenticatedUser()
  const {
    data: engagement,
    error,
    isError,
    isLoading,
    refetch,
  } = services.engagement.v3.getById(engagementId, { retry: shouldRetryShareLinkResolution })
  const targetApp = getShareLinkTargetApp(profile?.role)

  const resolution = useMemo(() => {
    if (!profile || !engagementId || !engagement) return
    const destinationPath = `engagement/${engagementId}${location.search}${location.hash}`
    if (canOpenAdminShareLink(profile.role)) {
      return { outcome: 'allowed' as const, destinationUrl: buildAppUrl('/admin/', destinationPath) }
    }
    if (canOpenCustomerShareLink(profile.role)) {
      return {
        outcome: 'allowed' as const,
        destinationUrl: buildAppUrl('/customer/', destinationPath),
      }
    }
    if (targetApp === 'mission-specialist' || targetApp === 'workforce-manager') {
      return UNSUPPORTED_ROLE_RESULT
    }
  }, [engagement, engagementId, location.hash, location.search, profile, targetApp])

  useLayoutEffect(() => {
    if (resolution?.outcome === 'allowed') {
      globalThis.location.href = resolution.destinationUrl
    }
  }, [resolution])

  useLayoutEffect(() => {
    if (!profile || isLoading || isError || engagement) return

    if (targetApp === 'customer' && engagementId) {
      globalThis.location.href = buildAppUrl(
        '/customer/',
        `engagement/${engagementId}${location.search}${location.hash}`
      )
      return
    }

    const destination = getEmptyEngagementDestination(targetApp)
    if (destination) globalThis.location.href = destination
  }, [engagement, engagementId, isError, isLoading, location.hash, location.search, profile, targetApp])

  useLayoutEffect(() => {
    if (!profile || !isError) return

    const destination = getEngagementErrorDestination(getHttpStatus(error), targetApp)
    if (destination) globalThis.location.href = destination
  }, [error, isError, profile, targetApp])

  if (!profile || isLoading || resolution?.outcome === 'allowed')
    return <ShareLinkAccessState state='loading' />
  if (isError) {
    const status = getHttpStatus(error)
    if (shouldWaitForEngagementRedirect(status, targetApp)) {
      return <ShareLinkAccessState state='loading' />
    }
    if (status === 400 || status === 404) {
      return <ShareLinkAccessState message={ENGAGEMENT_NOT_FOUND_RESULT.message} state='not-found' />
    }
    return <ShareLinkAccessState message={String(error)} onRetry={() => void refetch()} state='error' />
  }
  if (!resolution) {
    if (canRedirectToTargetApp(targetApp)) return <ShareLinkAccessState state='loading' />
    return <ShareLinkAccessState state='not-found' />
  }
  if (resolution.outcome === 'unsupported-role') {
    return <ShareLinkAccessState state='unsupported-role' />
  }

  return <ShareLinkAccessState state='loading' />
}
